// AidWaive, free-text form parsing helpers.
//
// All four user-facing fields (income, state, GPA, schools) are open
// text. We coerce them into the shapes the backend wants without ever
// changing what the user typed in the field.

import { US_STATES } from "./affordability";

/**
 * Parse free-text income strings into integer dollars.
 * "67k" -> 67000
 * "$67,000" -> 67000
 * "67000" -> 67000
 * Returns null if no usable number is present.
 */
export function parseIncome(raw) {
  if (raw == null) return null;
  const s = String(raw).trim().toLowerCase();
  if (!s) return null;
  // Strip currency, commas, spaces.
  const cleaned = s.replace(/[$,\s]/g, "");
  // Accept "67k", "67K", "67,000" -> handle "67k".
  const km = cleaned.match(/^([0-9]+(?:\.[0-9]+)?)([km])$/);
  if (km) {
    const n = parseFloat(km[1]);
    return Math.round(n * (km[2] === "k" ? 1000 : 1_000_000));
  }
  const num = parseFloat(cleaned);
  if (!Number.isFinite(num) || num <= 0) return null;
  return Math.round(num);
}

/** "3.7" / "3.7/4.0" / "B+" -> number (B+ -> 3.3). */
export function parseGpa(raw) {
  if (!raw) return null;
  const s = String(raw).trim().toUpperCase();
  // Letter grades map (rare but courteous).
  const letter = { "A+": 4.0, "A": 4.0, "A-": 3.7, "B+": 3.3, "B": 3.0, "B-": 2.7, "C+": 2.3, "C": 2.0, "C-": 1.7, "D": 1.0, "F": 0 };
  if (letter[s] != null) return letter[s];
  const m = s.match(/^([0-9](?:\.[0-9]+)?)/);
  if (!m) return null;
  const n = parseFloat(m[1]);
  if (!Number.isFinite(n) || n < 0 || n > 4.5) return null;
  return n;
}

/** Match a free-text state token to a US state code, or null. */
export function parseStateCode(raw) {
  if (!raw) return null;
  const s = String(raw).trim().toLowerCase();
  if (!s) return null;
  const upper = s.toUpperCase();
  // Exact two-letter code.
  const code = US_STATES.find(st => st.code === upper);
  if (code) return code.code;
  // Substring on full name, longer matches first.
  const matches = US_STATES.filter(st => st.name.toLowerCase().includes(s));
  matches.sort((a, b) => b.name.length - a.name.length);
  return matches.length ? matches[0].code : null;
}

/**
 * Resolve dream-school free-text against a list of seed schools by
 * fuzzy substring matching. Returns up to `limit` matched seed rows.
 */
export function resolveDreamSchools(text, schools, limit = 5) {
  if (!text || !schools) return [];
  const t = text.toLowerCase();
  const hits = [];
  const seen = new Set();
  for (const s of schools) {
    const name = (s.name || "").toLowerCase();
    if (!name) continue;
    // Match by full name OR by first significant token (skip "the", "of", "university").
    const tokens = name.split(/\s+/).filter(w => w.length > 3 && !["the","of","at","and","in","university","college"].includes(w));
    const hit = tokens.some(tok => t.includes(tok)) || t.includes(name);
    if (hit && !seen.has(s.id)) {
      hits.push(s);
      seen.add(s.id);
      if (hits.length >= limit) break;
    }
  }
  return hits;
}

/** Stable hash of an object for cache keys. */
export function hashInput(obj) {
  const s = JSON.stringify(obj);
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h) ^ s.charCodeAt(i);
  return (h >>> 0).toString(36);
}
